import { usePatientFollowUps } from '@/hooks/useFollowUps'
import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'
import { QUEUE_META, STATUS_META, eventLabel, sourceLabel } from './queue-meta'

interface PatientFollowUpsProps {
  patientId: number
}

/** Follow-up episode history rendered inside the patient profile page. */
export function PatientFollowUps({ patientId }: PatientFollowUpsProps) {
  const { data, isPending } = usePatientFollowUps(patientId)

  if (isPending) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-6 text-sm text-text-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading follow-up history...
      </div>
    )
  }

  const episodes = data?.episodes ?? []

  if (episodes.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-text-muted">
        No follow-up episodes for this patient yet.
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border bg-card divide-y divide-border">
      {episodes.map((ep) => {
        const queueMeta = QUEUE_META[ep.queue]
        const isOpen = ep.resolvedAt === null
        const statusLabel = isOpen ? 'In progress' : `Resolved: ${STATUS_META[ep.status].label}`
        return (
          <div key={ep.id}>
            <div className="flex flex-wrap items-center justify-between gap-2 bg-background/50 px-4 py-2.5 text-[11px] font-bold uppercase tracking-wide text-text-muted">
              <span className="flex items-center gap-2 normal-case tracking-normal text-text-dark text-sm font-semibold">
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold',
                    queueMeta.badgeClassName
                  )}
                >
                  {queueMeta.label}
                </span>
                {ep.triggerRef.packageName}
              </span>
              <span className="normal-case tracking-normal">
                {ep.assigneeName ?? 'Unassigned'} · {statusLabel}
              </span>
            </div>
            <ul className="divide-y divide-border-light">
              {[...ep.events]
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map((ev) => (
                  <li
                    key={ev.id}
                    className="grid grid-cols-[96px_1fr_auto] gap-3 px-4 py-2.5 text-[13px] items-start"
                  >
                    <span className="pt-0.5 font-mono text-[11.5px] text-text-muted whitespace-nowrap">
                      {new Date(ev.createdAt).toLocaleString('en-MY', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'Asia/Kuala_Lumpur',
                      })}
                    </span>
                    <div>
                      <div className="font-semibold">{eventLabel(ev.type, ev.source)}</div>
                      {ev.note && <div className="mt-0.5 text-xs text-text-muted">{ev.note}</div>}
                    </div>
                    <span
                      className={cn(
                        'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-extrabold tracking-wide',
                        ev.source === 'manual'
                          ? 'bg-muted text-text-muted'
                          : 'bg-success/10 text-success'
                      )}
                    >
                      {sourceLabel(ev.source)}
                    </span>
                  </li>
                ))}
            </ul>
          </div>
        )
      })}
      <div className="px-4 py-2 text-right text-[11px] text-text-muted">
        {episodes.length} episode{episodes.length === 1 ? '' : 's'} ·{' '}
        {episodes.filter((e) => e.status === 'converted').length} repurchased
      </div>
    </div>
  )
}
